import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DOW_LETT = ["L", "M", "M", "G", "V"]; // 0=Lun..4=Ven
const DOW_FULL = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];

const th = { border: "1px solid #cfd8dc", padding: "4px 6px", fontSize: 11, fontWeight: 700, background: "#eceff1", color: "#37474f", whiteSpace: "nowrap" };
const td = { border: "1px solid #e0e0e0", padding: "3px 6px", fontSize: 12, textAlign: "center" };

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function openPrintWindow(title, bodyHtml, landscape = false) {
  const w = window.open("", "_blank");
  if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
  const css = `
    @page { size: ${landscape ? "A4 landscape" : "A4 portrait"}; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #222; margin: 0; }
    h1 { font-size: 18px; margin: 0 0 2px; }
    h2 { font-size: 13px; font-weight: 600; color: #555; margin: 0 0 12px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #999; padding: 4px 6px; font-size: 11px; }
    th { background: #eceff1; }
    td.name { text-align: left; white-space: nowrap; font-weight: 600; }
    td.c { text-align: center; }
    .tot { margin-top: 10px; font-size: 12px; font-weight: 700; }
  `;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${css}</style></head><body>${bodyHtml}<scr` + `ipt>window.onload=function(){window.print();}</scr` + `ipt></body></html>`);
  w.document.close();
}

// Converte un intero in parole italiane (0..999999) per le ricevute.
function numeroInLettereInt(n) {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "zero";
  const u = ["", "uno", "due", "tre", "quattro", "cinque", "sei", "sette", "otto", "nove"];
  const sp = ["dieci", "undici", "dodici", "tredici", "quattordici", "quindici", "sedici", "diciassette", "diciotto", "diciannove"];
  const d = ["", "", "venti", "trenta", "quaranta", "cinquanta", "sessanta", "settanta", "ottanta", "novanta"];
  const sotto100 = (x) => {
    if (x < 10) return u[x];
    if (x < 20) return sp[x - 10];
    const dec = Math.floor(x / 10), un = x % 10;
    let w = d[dec];
    if (un === 1 || un === 8) w = w.slice(0, -1);
    if (un > 0) w += (un === 3 ? "tré" : u[un]);
    return w;
  };
  const sotto1000 = (x) => {
    const c = Math.floor(x / 100), r = x % 100;
    let w = "";
    if (c > 0) w = (c === 1 ? "cento" : u[c] + "cento");
    if (r > 0) w += sotto100(r);
    return w;
  };
  const m = Math.floor(n / 1000), r = n % 1000;
  let w = "";
  if (m > 0) w = (m === 1 ? "mille" : sotto1000(m) + "mila");
  if (r > 0) w += sotto1000(r);
  return w;
}

function importoInLettere(val) {
  const tot = Math.round((Number(val) || 0) * 100);
  const euros = Math.floor(tot / 100);
  const cents = tot % 100;
  return `${numeroInLettereInt(euros)}/${String(cents).padStart(2, "0")}`;
}

// Ricevuta generica NON intestata per un pagamento.
function stampaRicevuta({ nome, importo, nota }) {
  const imp = (Number(importo) || 0).toFixed(2).replace(".", ",");
  const lettere = importoInLettere(importo);
  const oggi = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  const causale = (nota || "").trim() || "pasti mensa docenti";
  const body = `
    <div style="max-width:150mm;margin:0 auto;">
      <h1 style="text-align:center;letter-spacing:3px;border-bottom:2px solid #222;padding-bottom:6px;">RICEVUTA</h1>
      <div style="height:20mm;"></div>
      <p style="font-size:16px;line-height:2.2;">
        Ricevo da <b>${esc(nome)}</b> la somma di <b>&euro; ${esc(imp)}</b>
        (<i>euro ${esc(lettere)}</i>) per <b>${esc(causale)}</b>.
      </p>
      <div style="height:18mm;"></div>
      <p style="font-size:16px;">Brescia, ${esc(oggi)}</p>
      <div style="height:24mm;"></div>
      <div style="text-align:right;font-size:15px;">Firma ____________________________</div>
    </div>`;
  openPrintWindow(`Ricevuta - ${nome}`, body);
}

export default function PresenzeMensaDocentiPage({ gestione = false }) {
  const now = new Date();
  const { user } = useAuth();
  // Funzioni di gestione (listino prezzi, colonna €, stampa importi, riepilogo annuale):
  // disponibili SOLO nella rotta dedicata /mensa-docenti (prop gestione), per admin o utenti col permesso "mense_docenti".
  // Su /portineria/presenze (vista Portineria) resta solo il tab "Mesi" senza prezzi.
  const isAdmin = !!gestione && (user?.role === "admin" || !!user?.gestioni?.mense_docenti);
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [listino, setListino] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("tutti"); // "attivi" | "tutti" — si apre su Tutti (così i docenti nuovi si vedono subito)
  const [dayPopup, setDayPopup] = useState(null); // { cod, nome, giorno, dow, ora, nota, tipoId, tipo, libero, prezzo }
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ cognome: "", nome: "" });
  const [view, setView] = useState("mesi"); // "anno" | "mesi" | "listino" — menu a 3 schede
  const [riepAnno, setRiepAnno] = useState((now.getMonth() + 1) >= 9 ? now.getFullYear() : now.getFullYear() - 1);
  const [riep, setRiep] = useState(null);
  const [pagPopup, setPagPopup] = useState(null);
  const [riepLoading, setRiepLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/portineria/presenze-mensa", { params: { anno, mese } });
      setData(r.data);
      setListino(r.data?.listino || []);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [anno, mese]);

  useEffect(() => { load(); }, [load]);

  const fetchRiep = useCallback(async () => {
    if (!isAdmin) return;
    setRiepLoading(true);
    try { const r = await api.get("/portineria/riepilogo-annuale", { params: { anno_inizio: riepAnno } }); setRiep(r.data); }
    catch { setRiep(null); }
    finally { setRiepLoading(false); }
  }, [isAdmin, riepAnno]);
  useEffect(() => { fetchRiep(); }, [fetchRiep]);

  const saveCredito = async (codice, credito) => {
    try { await api.put("/portineria/mensa-conto/credito", { anno_inizio: riepAnno, codice, credito }); await fetchRiep(); }
    catch { alert("Errore nel salvataggio delle mense a credito"); }
  };
  const saveSaldoIniziale = async (codice, saldo_iniziale) => {
    try { await api.put("/portineria/mensa-conto/saldo-iniziale", { anno_inizio: riepAnno, codice, saldo_iniziale }); await fetchRiep(); }
    catch { alert("Errore nel salvataggio del saldo iniziale"); }
  };
  const savePagamenti = async (codice, pagamenti) => {
    try { await api.put("/portineria/mensa-conto/pagamenti", { anno_inizio: riepAnno, codice, pagamenti }); await fetchRiep(); return true; }
    catch { alert("Errore nel salvataggio dei pagamenti"); return false; }
  };

  const presenze = data?.presenze || {};
  const giorni = data?.giorni || [];
  const docenti = data?.docenti || [];
  const todayDay = (anno === now.getFullYear() && mese === now.getMonth() + 1) ? now.getDate() : null;

  const setLocalPres = (cod, patch) => {
    setData(d => {
      const p = { ...(d.presenze || {}) };
      p[cod] = { giorni: [], dettagli: {}, saldato: false, saldato_data: "", saldato_nota: "", ...(p[cod] || {}), ...patch };
      return { ...d, presenze: p };
    });
  };

  // Un docente è "attivo" se ha mangiato almeno una volta nell'anno (dato dal backend)
  // oppure se ha presenze nel mese corrente appena segnate (aggiornamento ottimistico).
  const isAttivo = (d) => !!d.attivo || ((presenze[d.codice]?.giorni || []).length > 0);

  const onDayClick = (d, g) => {
    const p = presenze[d.codice] || {};
    const on = (p.giorni || []).includes(g.giorno);
    if (on) {
      // Casella già imputata: apri il popup precompilato per vedere/modificare (niente cancellazione immediata).
      const det = (p.dettagli || {})[g.giorno] || {};
      const li = listino.find(x => x.nome === det.tipo) || null;
      setDayPopup({
        cod: d.codice, nome: `${d.cognome} ${d.nome}`, giorno: g.giorno, dow: g.dow,
        ora: det.ora || "", nota: det.nota || "",
        tipoId: li ? li.id : "", tipo: det.tipo || "",
        libero: li ? !!li.libero : false,
        prezzo: det.prezzo != null ? det.prezzo : "",
        editing: true,
      });
      return;
    }
    setDayPopup({ cod: d.codice, nome: `${d.cognome} ${d.nome}`, giorno: g.giorno, dow: g.dow, ora: "", nota: "", tipoId: "", tipo: "", libero: false, prezzo: "", editing: false });
  };

  const deleteDayFromPopup = () => {
    if (!dayPopup) return;
    const { cod, giorno } = dayPopup;
    setDayPopup(null);
    removeDay(cod, giorno);
  };

  const removeDay = async (cod, giorno) => {
    const p = presenze[cod] || {};
    const nextG = (p.giorni || []).filter(x => x !== giorno);
    const nextDet = { ...(p.dettagli || {}) }; delete nextDet[giorno];
    setLocalPres(cod, { giorni: nextG, dettagli: nextDet });
    try { await api.post("/portineria/presenze-mensa/toggle", { anno, mese, docente_codice: cod, giorno, presente: false }); }
    catch { load(); }
  };

  const confirmDay = async () => {
    const { cod, giorno, ora, nota, tipoId, tipo, libero } = dayPopup;
    if (!tipoId) { alert("Seleziona il tipo di pasto."); return; }
    if (libero && (dayPopup.prezzo === "" || dayPopup.prezzo === null || isNaN(Number(dayPopup.prezzo)))) {
      alert("Inserisci l'importo del pasto libero."); return;
    }
    const prezzo = Number(dayPopup.prezzo) || 0;
    const p = presenze[cod] || {};
    const nextG = [...new Set([...(p.giorni || []), giorno])].sort((a, b) => a - b);
    const nextDet = { ...(p.dettagli || {}), [giorno]: { ora: (ora || "").trim(), nota: (nota || "").trim(), tipo, prezzo } };
    setLocalPres(cod, { giorni: nextG, dettagli: nextDet });
    setDayPopup(null);
    try { await api.post("/portineria/presenze-mensa/toggle", { anno, mese, docente_codice: cod, giorno, presente: true, ora: (ora || "").trim(), nota: (nota || "").trim(), tipo, prezzo }); }
    catch { load(); }
  };

  const selectPasto = (it) => setDayPopup(s => ({ ...s, tipoId: it.id, tipo: it.nome, libero: !!it.libero, prezzo: it.libero ? (s.prezzo ?? "") : it.prezzo }));

  const saveListino = async (next) => {
    try { const r = await api.put("/portineria/listino", { listino: next }); setListino(r.data?.listino || next); return true; }
    catch (e) { alert("Errore salvataggio listino: " + (e.response?.data?.detail || e.message)); return false; }
  };

  const saveGuest = async () => {
    const cognome = guestForm.cognome.trim(), nome = guestForm.nome.trim();
    if (!cognome && !nome) { alert("Inserisci almeno cognome o nome"); return; }
    try {
      const r = await api.post("/portineria/ospiti", { cognome, nome });
      const g = { codice: r.data.codice, cognome: r.data.cognome, nome: r.data.nome, ospite: true, attivo: false };
      setData(d => ({ ...d, docenti: [...(d.docenti || []), g].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, "it")) }));
      setGuestOpen(false); setGuestForm({ cognome: "", nome: "" });
      setTab("tutti"); setSearch(cognome || nome);
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteGuest = async (cod) => {
    if (!window.confirm("Eliminare questo nome aggiunto manualmente?")) return;
    try {
      await api.delete(`/portineria/ospiti/${encodeURIComponent(cod)}`);
      setData(d => { const docs = (d.docenti || []).filter(x => x.codice !== cod); const pr = { ...(d.presenze || {}) }; delete pr[cod]; return { ...d, docenti: docs, presenze: pr }; });
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const attiviCount = useMemo(() => docenti.filter(isAttivo).length, [docenti, presenze]); // eslint-disable-line

  const visibleDocenti = useMemo(() => {
    const s = search.trim().toLowerCase();
    return docenti.filter(d => {
      if (tab === "attivi" && !isAttivo(d)) return false;
      if (s && !`${d.cognome} ${d.nome}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [docenti, presenze, search, tab]); // eslint-disable-line

  const euroDocente = (p) => {
    const det = p?.dettagli || {};
    return (p?.giorni || []).reduce((s, g) => s + (Number((det[g] || {}).prezzo) || 0), 0);
  };

  const totali = useMemo(() => {
    let np = 0, tot = 0;
    visibleDocenti.forEach(d => { const p = presenze[d.codice] || {}; np += (p.giorni || []).length; tot += euroDocente(p); });
    return { np, tot };
  }, [visibleDocenti, presenze]); // eslint-disable-line

  const anni = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y--) anni.push(y);

  // Menu principale a 3 schede. "Anno scolastico" e "Listino" sono funzioni di gestione.
  const topTabs = [
    { key: "anno", label: "📊 Anno scolastico" },
    { key: "mesi", label: "🗓 Mesi" },
    { key: "listino", label: "⚙ Listino" },
  ].filter(t => t.key === "mesi" || isAdmin);

  // ─── Stampe ───────────────────────────────────────────────────────────────
  const stampaGiorno = (g) => {
    const present = docenti
      .filter(d => (presenze[d.codice]?.giorni || []).includes(g.giorno))
      .sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, "it"));
    let totE = 0;
    const rows = present.map((d, i) => {
      const det = (presenze[d.codice]?.dettagli || {})[g.giorno] || {};
      totE += Number(det.prezzo) || 0;
      const prezzoCell = isAdmin ? `<td class="c">${(Number(det.prezzo) || 0).toFixed(2)}</td>` : "";
      return `<tr><td class="c">${i + 1}</td><td class="name">${esc(d.cognome)} ${esc(d.nome)}</td><td class="c">${esc(det.ora || "")}</td><td class="c">${esc(det.tipo || "")}</td><td>${esc(det.nota || "")}</td>${prezzoCell}</tr>`;
    }).join("");
    const prezzoHead = isAdmin ? `<th style="width:60px">Prezzo</th>` : "";
    const colspan = isAdmin ? 6 : 5;
    const totRow = isAdmin
      ? `<div class="tot">Totale presenti: ${present.length} · Totale € ${totE.toFixed(2)}</div>`
      : `<div class="tot">Totale presenti: ${present.length}</div>`;
    const body = `
      <h1>Presenti mensa docenti</h1>
      <h2>${DOW_FULL[g.dow]} ${g.giorno} ${MESI[mese - 1]} ${anno}</h2>
      <table>
        <thead><tr><th style="width:30px">#</th><th style="text-align:left">Cognome Nome</th><th style="width:60px">Ora</th><th style="width:90px">Tipo pasto</th><th>Nota</th>${prezzoHead}</tr></thead>
        <tbody>${rows || `<tr><td colspan="${colspan}" class="c">Nessun presente</td></tr>`}</tbody>
      </table>
      ${totRow}`;
    openPrintWindow(`Presenti ${g.giorno}-${mese}-${anno}`, body, false);
  };

  const stampaMese = () => {
    const eaters = docenti
      .filter(d => (presenze[d.codice]?.giorni || []).length > 0)
      .sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, "it"));
    const headDays = giorni.map(g => `<th class="c" style="width:22px">${g.giorno}<br><small>${DOW_LETT[g.dow]}</small></th>`).join("");
    let totN = 0, totE = 0;
    const rows = eaters.map(d => {
      const p = presenze[d.codice] || {};
      const cells = giorni.map(g => `<td class="c">${(p.giorni || []).includes(g.giorno) ? "✓" : ""}</td>`).join("");
      const n = (p.giorni || []).length; const e = euroDocente(p); totN += n; totE += e;
      return `<tr><td class="name">${esc(d.cognome)} ${esc(d.nome)}</td>${cells}<td class="c">${n}</td><td class="c">${e.toFixed(2)}</td></tr>`;
    }).join("");
    const body = `
      <h1>Tabellone mensa docenti — ${MESI[mese - 1]} ${anno}</h1>
      <h2>Solo docenti che hanno mangiato (${eaters.length})</h2>
      <table>
        <thead><tr><th style="text-align:left">Docente</th>${headDays}<th class="c">N°</th><th class="c">€</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="${giorni.length + 3}" class="c">Nessun docente ha mangiato in questo mese</td></tr>`}</tbody>
        <tfoot><tr><td class="name">TOTALE</td>${giorni.map(() => '<td></td>').join("")}<td class="c">${totN}</td><td class="c">${totE.toFixed(2)}</td></tr></tfoot>
      </table>`;
    openPrintWindow(`Tabellone ${MESI[mese - 1]} ${anno}`, body, true);
  };

  const tabBtn = (key, label) => (
    <button type="button" data-testid={`pmd-tab-${key}`} onClick={() => setTab(key)}
      style={{
        padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1px solid #b0bec5",
        borderBottom: tab === key ? "2px solid #00897b" : "1px solid #b0bec5",
        background: tab === key ? "#e0f2f1" : "#fff", color: tab === key ? "#00695c" : "#607d8b",
        borderRadius: "6px 6px 0 0", marginRight: 4,
      }}>{label}</button>
  );

  return (
    <div data-testid="presenze-mensa-docenti" style={{ padding: 12 }}>
      {topTabs.length > 1 && (
        <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 14, borderBottom: "2px solid #00897b" }}>
          {topTabs.map(t => (
            <button key={t.key} type="button" data-testid={`pmd-view-${t.key}`} onClick={() => setView(t.key)}
              style={{
                padding: "9px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", border: "1px solid #b0bec5",
                borderBottom: view === t.key ? "2px solid #00897b" : "1px solid #b0bec5", marginBottom: -2,
                background: view === t.key ? "#e0f2f1" : "#fff", color: view === t.key ? "#00695c" : "#607d8b",
                borderRadius: "6px 6px 0 0", marginRight: 4,
              }}>{t.label}</button>
          ))}
        </div>
      )}

      {view === "mesi" && (
      <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#78909c", textTransform: "uppercase" }}>Anno</div>
          <select data-testid="pmd-anno" value={anno} onChange={e => setAnno(Number(e.target.value))}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 13 }}>
            {anni.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#78909c", textTransform: "uppercase" }}>Mese</div>
          <select data-testid="pmd-mese" value={mese} onChange={e => setMese(Number(e.target.value))}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 13 }}>
            {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#78909c", textTransform: "uppercase" }}>Cerca cognome / nome</div>
          <input data-testid="pmd-search" type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca docente…"
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 13, width: "100%" }} />
        </div>
        <button type="button" data-testid="pmd-add-guest" onClick={() => { setGuestForm({ cognome: "", nome: "" }); setGuestOpen(true); }}
          title="Aggiungi un nome non presente tra i docenti (ospite / esterno)"
          style={{ padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "#00695c", background: "white", border: "1px solid #00897b", borderRadius: 4, cursor: "pointer" }}>
          ➕ Aggiungi nome
        </button>
        {isAdmin && (
          <button type="button" data-testid="pmd-print-mese" onClick={stampaMese}
            title="Stampa il tabellone del mese (solo chi ha mangiato)"
            style={{ padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "white", background: "#00695c", border: "none", borderRadius: 4, cursor: "pointer" }}>
            🖨️ Stampa tabellone mese
          </button>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: -1 }}>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          {tabBtn("attivi", `Attivi (${attiviCount})`)}
          {tabBtn("tutti", `Tutti (${docenti.length})`)}
        </div>
        <div style={{ fontSize: 12, color: "#455a64", textAlign: "right", paddingBottom: 4 }}>
          <span style={{ marginRight: 14 }}>Presenze: <b data-testid="pmd-tot-presenze">{totali.np}</b></span>
          {isAdmin && <span>Addebito: <b data-testid="pmd-tot-euro">€ {totali.tot.toFixed(2)}</b></span>}
        </div>
      </div>

      {loading && <div style={{ padding: 20, color: "#888" }}>Caricamento…</div>}

      {!loading && (
        <div style={{ overflowX: "auto", border: "1px solid #cfd8dc" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }} data-testid="pmd-table">
            <thead>
              <tr>
                <th style={{ ...th, position: "sticky", left: 0, zIndex: 2, minWidth: 190, textAlign: "left" }}>DOCENTE</th>
                {giorni.map(g => {
                  const isToday = g.giorno === todayDay;
                  return (
                  <th key={g.giorno} style={{ ...th, minWidth: 30, ...(isToday ? { background: "#fb8c00", color: "white", boxShadow: "inset 2px 0 0 #e65100, inset -2px 0 0 #e65100" } : {}) }}
                    data-testid={isToday ? "pmd-today-header" : undefined}>
                    <div>{g.giorno}</div>
                    <div style={{ fontSize: 9, color: isToday ? "#fff3e0" : "#90a4ae" }}>{DOW_LETT[g.dow]}</div>
                    {isToday && <div style={{ fontSize: 8, fontWeight: 700, color: "white", letterSpacing: 0.5 }}>OGGI</div>}
                    <button type="button" onClick={() => stampaGiorno(g)} data-testid={`pmd-print-giorno-${g.giorno}`}
                      title={`Stampa l'elenco dei presenti in mensa del ${g.giorno}/${mese} (cognome nome / orario / note)`}
                      style={{ marginTop: 3, border: `1px solid ${isToday ? "rgba(255,255,255,0.7)" : "#80cbc4"}`, background: isToday ? "rgba(255,255,255,0.22)" : "#e0f2f1", color: isToday ? "white" : "#00695c", cursor: "pointer", fontSize: 11, lineHeight: 1, borderRadius: 3, padding: "2px 5px" }}>🖨️</button>
                  </th>
                  );
                })}
                <th style={{ ...th, minWidth: 40 }}>N°</th>
                {isAdmin && <th style={{ ...th, minWidth: 70 }}>€</th>}
              </tr>
            </thead>
            <tbody>
              {visibleDocenti.map(d => {
                const p = presenze[d.codice] || { giorni: [], dettagli: {} };
                const n = (p.giorni || []).length;
                return (
                  <tr key={d.codice} data-testid={`pmd-row-${d.codice}`}>
                    <td style={{ ...td, position: "sticky", left: 0, zIndex: 1, background: d.ospite ? "#fff8e1" : "#fafafa", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {d.cognome} {d.nome}
                      {d.ospite && (
                        <>
                          <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#e65100", background: "#ffe0b2", padding: "1px 5px", borderRadius: 8, verticalAlign: "middle" }}>ospite</span>
                          <button type="button" onClick={() => deleteGuest(d.codice)} data-testid={`pmd-del-ospite-${d.codice}`}
                            title="Elimina questo nome" style={{ marginLeft: 4, border: "none", background: "transparent", cursor: "pointer", fontSize: 11, color: "#c62828" }}>🗑</button>
                        </>
                      )}
                    </td>
                    {giorni.map(g => {
                      const on = (p.giorni || []).includes(g.giorno);
                      const det = (p.dettagli || {})[g.giorno] || {};
                      const isToday = g.giorno === todayDay;
                      return (
                        <td key={g.giorno} style={{ ...td, padding: 0, background: on ? "#e8f5e9" : (isToday ? "#fff8e1" : "white"), ...(isToday ? { boxShadow: "inset 2px 0 0 #ffb74d, inset -2px 0 0 #ffb74d" } : {}) }}>
                          <button type="button" onClick={() => onDayClick(d, g)}
                            data-testid={`pmd-check-${d.codice}-${g.giorno}`}
                            title={on ? `${det.ora || ""} ${det.nota || ""}`.trim() || "Presente — clicca per vedere/modificare" : "Segna presenza"}
                            style={{ width: "100%", minHeight: 28, border: "none", background: "transparent", cursor: "pointer", color: on ? "#1b5e20" : "#cfd8dc", fontWeight: 700, fontSize: 14, padding: "2px 0", lineHeight: 1 }}>
                            {on ? "✓" : "+"}
                            {on && det.ora ? <div style={{ fontSize: 8, color: "#558b2f", fontWeight: 600 }}>{det.ora}</div> : null}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ ...td, fontWeight: 700 }}>{n}</td>
                    {isAdmin && <td style={{ ...td, fontWeight: 700, color: "#1b5e20" }}>{euroDocente(p).toFixed(2)}</td>}
                  </tr>
                );
              })}
              {visibleDocenti.length === 0 && (
                <tr><td colSpan={giorni.length + (isAdmin ? 3 : 2)} style={{ ...td, color: "#888", padding: 20 }}>
                  {tab === "attivi" ? "Nessun docente attivo (ancora nessuno ha mangiato quest'anno). Passa a «Tutti» per segnare le presenze." : "Nessun docente."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {view === "anno" && isAdmin && (
        <div data-testid="pmd-riepilogo">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: "#37474f" }}>📊 Riepilogo annuale addebiti</h3>
            <label style={{ fontSize: 12, color: "#607d8b" }}>Anno scolastico:&nbsp;
              <select value={riepAnno} onChange={e => setRiepAnno(Number(e.target.value))} data-testid="pmd-riep-anno"
                style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 13 }}>
                {Array.from({ length: 7 }, (_, k) => now.getFullYear() + 1 - k).map(y => {
                  const lab = `${y}/${String(y + 1).slice(-2)}`;
                  return <option key={y} value={y}>{lab}</option>;
                })}
              </select>
            </label>
            <button type="button" onClick={fetchRiep} data-testid="pmd-riep-refresh"
              style={{ padding: "5px 12px", fontSize: 12, fontWeight: 700, color: "#00695c", background: "white", border: "1px solid #00897b", borderRadius: 4, cursor: "pointer" }}>🔄 Aggiorna</button>
            {riep && <span style={{ marginLeft: "auto", fontSize: 12, color: "#455a64" }}>Dovuto netto <b style={{ color: "#1b5e20" }}>€ {(riep.tot_dovuto_net != null ? riep.tot_dovuto_net : riep.tot_dovuto).toFixed(2)}</b> · Pagato <b style={{ color: "#1565c0" }}>€ {(riep.tot_pagamenti || 0).toFixed(2)}</b> · Saldo <b style={{ color: (riep.tot_saldo || 0) > 0.001 ? "#c62828" : "#1b5e20" }}>€ {(riep.tot_saldo || 0).toFixed(2)}</b></span>}
          </div>
          {riepLoading ? <div style={{ padding: 16, color: "#888" }}>Caricamento riepilogo…</div> : (
            <div style={{ overflowX: "auto", border: "1px solid #cfd8dc" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }} data-testid="pmd-riep-table">
                <thead>
                  <tr>
                    <th style={{ ...th, position: "sticky", left: 0, zIndex: 2, minWidth: 190, textAlign: "left" }}>DOCENTE</th>
                    <th style={{ ...th, minWidth: 84, background: "#eceff1" }}>SALDO<br/>INIZIALE</th>
                    <th style={{ ...th, minWidth: 70, background: "#fff3e0" }}>MENSE A<br/>CREDITO</th>
                    {(riep?.months || []).map(mo => <th key={mo.key} style={{ ...th, minWidth: 64 }}>{mo.mese}/{mo.anno}</th>)}
                    <th style={{ ...th, minWidth: 80, background: "#d7ccc8" }}>TOTALE<br/>(netto)</th>
                    <th style={{ ...th, minWidth: 90, background: "#e3f2fd" }}>PAGAMENTI</th>
                    <th style={{ ...th, minWidth: 90, background: "#ffebee" }}>SALDO<br/>DOVUTO</th>
                  </tr>
                </thead>
                <tbody>
                  {(riep?.rows || []).map(r => (
                    <tr key={r.codice} data-testid={`pmd-riep-row-${r.codice}`}>
                      <td style={{ ...td, position: "sticky", left: 0, zIndex: 1, background: r.ospite ? "#fff8e1" : "#fafafa", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {r.cognome} {r.nome}{r.ospite && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#e65100", background: "#ffe0b2", padding: "1px 5px", borderRadius: 8 }}>ospite</span>}
                      </td>
                      <td style={{ ...td, background: "#f5f6f7" }}>
                        <input type="number" step="0.01" defaultValue={r.saldo_iniziale || 0} data-testid={`pmd-saldo-iniziale-${r.codice}`}
                          key={`si-${r.codice}-${r.saldo_iniziale || 0}`}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                          onBlur={e => { const v = Math.round((parseFloat(e.target.value) || 0) * 100) / 100; if (v !== (r.saldo_iniziale || 0)) saveSaldoIniziale(r.codice, v); }}
                          style={{ width: 64, padding: "4px 6px", border: "1px solid #90a4ae", borderRadius: 4, fontSize: 12, textAlign: "center" }} />
                      </td>
                      <td style={{ ...td, background: "#fff8f0" }}>
                        <input type="number" min="0" defaultValue={r.credito || 0} data-testid={`pmd-credito-${r.codice}`}
                          key={`cred-${r.codice}-${r.credito || 0}`}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                          onBlur={e => { const v = Math.max(0, parseInt(e.target.value, 10) || 0); if (v !== (r.credito || 0)) saveCredito(r.codice, v); }}
                          style={{ width: 52, padding: "4px 6px", border: "1px solid #ffb74d", borderRadius: 4, fontSize: 12, textAlign: "center" }} />
                        {r.credito > 0 && <div style={{ fontSize: 9, color: "#c62828", marginTop: 1 }}>-€ {(r.credito_valore || 0).toFixed(2)}</div>}
                      </td>
                      {(riep?.months || []).map(mo => {
                        const c = r.per_mese[mo.key] || { pasti: 0, dovuto: 0 };
                        return (
                          <td key={mo.key} style={{ ...td }}>
                            {c.pasti > 0 ? (<><div style={{ fontWeight: 700 }}>{c.pasti}</div><div style={{ fontSize: 11, color: "#1b5e20" }}>€ {c.dovuto.toFixed(2)}</div></>) : <span style={{ color: "#ccc" }}>–</span>}
                          </td>
                        );
                      })}
                      <td style={{ ...td, background: "#efebe9", fontWeight: 700 }}>
                        <div>{r.tot_pasti_net != null ? r.tot_pasti_net : r.tot_pasti}</div>
                        <div style={{ fontSize: 11, color: "#1b5e20" }}>€ {(r.tot_dovuto_net != null ? r.tot_dovuto_net : r.tot_dovuto).toFixed(2)}</div>
                        {r.credito > 0 && <div style={{ fontSize: 9, color: "#999", textDecoration: "line-through" }}>€ {r.tot_dovuto.toFixed(2)}</div>}
                      </td>
                      <td style={{ ...td, background: "#f5fbff" }}>
                        <div style={{ fontWeight: 700, color: "#1565c0" }}>€ {(r.totale_pagamenti || 0).toFixed(2)}</div>
                        <button type="button" data-testid={`pmd-pag-btn-${r.codice}`}
                          onClick={() => setPagPopup({ codice: r.codice, nome: `${r.cognome} ${r.nome}`, rows: (r.pagamenti || []).map(x => ({ ...x })) })}
                          style={{ marginTop: 2, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: "#1565c0", background: "white", border: "1px solid #1565c0", borderRadius: 3, cursor: "pointer" }}>💳 Pagamenti</button>
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: (r.saldo_dovuto || 0) > 0.001 ? "#c62828" : "#1b5e20" }}>€ {(r.saldo_dovuto || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {(!riep || riep.rows.length === 0) && (
                    <tr><td colSpan={(riep?.months.length || 10) + 6} style={{ ...td, color: "#888", padding: 20 }}>Nessun docente con addebiti nell'anno scolastico selezionato.</td></tr>
                  )}
                </tbody>
                {riep && riep.rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={{ ...td, position: "sticky", left: 0, background: "#eceff1", textAlign: "left", fontWeight: 700 }}>TOTALE</td>
                      <td style={{ ...td, background: "#eceff1", fontWeight: 700 }}>€ {(riep.tot_saldo_iniziale || 0).toFixed(2)}</td>
                      <td style={{ ...td, background: "#eceff1" }}></td>
                      {riep.months.map(mo => {
                        const t = riep.totali_mese[mo.key] || { pasti: 0, dovuto: 0 };
                        return <td key={mo.key} style={{ ...td, background: "#eceff1", fontWeight: 700 }}><div>{t.pasti}</div><div style={{ fontSize: 11, color: "#1b5e20" }}>€ {t.dovuto.toFixed(2)}</div></td>;
                      })}
                      <td style={{ ...td, background: "#d7ccc8", fontWeight: 700 }}><div>{riep.tot_pasti_net != null ? riep.tot_pasti_net : riep.tot_pasti}</div><div style={{ fontSize: 11, color: "#1b5e20" }}>€ {(riep.tot_dovuto_net != null ? riep.tot_dovuto_net : riep.tot_dovuto).toFixed(2)}</div></td>
                      <td style={{ ...td, background: "#e3f2fd", fontWeight: 700, color: "#1565c0" }}>€ {(riep.tot_pagamenti || 0).toFixed(2)}</td>
                      <td style={{ ...td, background: "#ffebee", fontWeight: 700, color: (riep.tot_saldo || 0) > 0.001 ? "#c62828" : "#1b5e20" }}>€ {(riep.tot_saldo || 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}

      {view === "listino" && isAdmin && (
        <ListinoPanel listino={listino} onSave={saveListino} />
      )}

      {pagPopup && (
        <PagamentiPopup nome={pagPopup.nome} rows={pagPopup.rows}
          onSave={(rows) => savePagamenti(pagPopup.codice, rows)} onClose={() => setPagPopup(null)} />
      )}

      {dayPopup && (
        <div onMouseDown={() => setDayPopup(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div data-testid="pmd-day-popup" onMouseDown={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, width: 420, maxWidth: "94vw", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#00897b", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🍴 Presenza mensa</div>
              <button type="button" onClick={() => setDayPopup(null)} data-testid="pmd-day-close"
                style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#444" }}>
                <b style={{ textTransform: "uppercase" }}>{dayPopup.nome}</b><br />
                {DOW_FULL[dayPopup.dow]} {dayPopup.giorno} {MESI[mese - 1]} {anno}
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#555", fontWeight: 700, display: "block", marginBottom: 2 }}>Orario pasto</label>
                <input type="time" value={dayPopup.ora} data-testid="pmd-day-ora" autoFocus
                  onChange={e => setDayPopup(s => ({ ...s, ora: e.target.value }))}
                  style={{ padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#555", fontWeight: 700, display: "block", marginBottom: 4 }}>Tipo pasto *</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {listino.map(it => (
                    <label key={it.id} data-testid={`pmd-pasto-${it.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", padding: "5px 8px", border: `1px solid ${dayPopup.tipoId === it.id ? "#00897b" : "#e0e0e0"}`, borderRadius: 4, background: dayPopup.tipoId === it.id ? "#e0f2f1" : "white" }}>
                      <input type="radio" name="pmd-pasto" checked={dayPopup.tipoId === it.id} onChange={() => selectPasto(it)} />
                      <span style={{ fontWeight: 600 }}>{it.nome}</span>
                      <span style={{ marginLeft: "auto", color: "#607d8b", fontSize: 12 }}>{it.libero ? "importo libero" : `€ ${Number(it.prezzo).toFixed(2)}`}</span>
                    </label>
                  ))}
                  {listino.length === 0 && <div style={{ fontSize: 12, color: "#b71c1c" }}>Nessun listino configurato. Chiedi all'amministratore di impostarlo.</div>}
                </div>
                {dayPopup.libero && (
                  <input type="number" step="0.01" min="0" value={dayPopup.prezzo} data-testid="pmd-day-prezzo-libero"
                    onChange={e => setDayPopup(s => ({ ...s, prezzo: e.target.value }))}
                    placeholder="Importo € (libero)" autoFocus
                    style={{ marginTop: 6, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: "100%" }} />
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#555", fontWeight: 700, display: "block", marginBottom: 2 }}>Nota</label>
                <input type="text" value={dayPopup.nota} data-testid="pmd-day-nota"
                  onChange={e => setDayPopup(s => ({ ...s, nota: e.target.value }))}
                  placeholder="es. senza glutine, ospite…"
                  onKeyDown={e => { if (e.key === "Enter") confirmDay(); }}
                  style={{ padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: "100%" }} />
              </div>
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: dayPopup.editing ? "space-between" : "flex-end", alignItems: "center", gap: 8 }}>
              {dayPopup.editing ? (
                <button type="button" onClick={deleteDayFromPopup} data-testid="pmd-day-delete"
                  style={{ padding: "8px 16px", background: "white", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🗑 Cancella</button>
              ) : (
                <button type="button" onClick={() => setDayPopup(null)} data-testid="pmd-day-cancel"
                  style={{ padding: "8px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}>Annulla</button>
              )}
              <button type="button" onClick={confirmDay} data-testid="pmd-day-confirm"
                disabled={!dayPopup.tipoId || (dayPopup.libero && (dayPopup.prezzo === "" || dayPopup.prezzo === null))}
                style={{ padding: "8px 20px", background: (!dayPopup.tipoId || (dayPopup.libero && (dayPopup.prezzo === "" || dayPopup.prezzo === null))) ? "#80cbc4" : "#00897b", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✓ Conferma</button>
            </div>
          </div>
        </div>
      )}
      {guestOpen && (
        <div onMouseDown={() => setGuestOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div data-testid="pmd-guest-modal" onMouseDown={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, width: 420, maxWidth: "94vw", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#00695c", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>➕ Aggiungi nome (non docente)</div>
              <button type="button" onClick={() => setGuestOpen(false)} data-testid="pmd-guest-close"
                style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, color: "#555", fontWeight: 700, display: "block", marginBottom: 2 }}>Cognome</label>
                <input type="text" value={guestForm.cognome} data-testid="pmd-guest-cognome" autoFocus
                  onChange={e => setGuestForm(f => ({ ...f, cognome: e.target.value }))}
                  style={{ padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#555", fontWeight: 700, display: "block", marginBottom: 2 }}>Nome</label>
                <input type="text" value={guestForm.nome} data-testid="pmd-guest-nome"
                  onChange={e => setGuestForm(f => ({ ...f, nome: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") saveGuest(); }}
                  style={{ padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: "100%" }} />
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>Il nome verrà aggiunto al tabellone come «ospite» e potrai segnarne le presenze come per i docenti.</div>
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => setGuestOpen(false)} data-testid="pmd-guest-cancel"
                style={{ padding: "8px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}>Annulla</button>
              <button type="button" onClick={saveGuest} data-testid="pmd-guest-save"
                style={{ padding: "8px 20px", background: "#00695c", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Aggiungi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListinoPanel({ listino, onSave }) {
  const [rows, setRows] = useState(() => (listino || []).map(x => ({ ...x })));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const upd = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, { id: "voce_" + Date.now(), nome: "", prezzo: 0, libero: false }]);
  const delRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const save = async () => {
    const cleaned = rows.filter(r => (r.nome || "").trim());
    if (!cleaned.length) { alert("Inserisci almeno una voce con un nome"); return; }
    setBusy(true);
    const ok = await onSave(cleaned.map(r => ({ id: r.id, nome: (r.nome || "").trim(), prezzo: Number(r.prezzo) || 0, libero: !!r.libero })));
    setBusy(false);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
  };
  return (
    <div data-testid="pmd-listino-panel" style={{ maxWidth: 560, border: "1px solid #cfd8dc", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: "#00695c", color: "white", fontWeight: 700, fontSize: 14 }}>⚙ Listino prezzi pasti</div>
      <div style={{ padding: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "#607d8b", fontSize: 11 }}>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>NOME PASTO</th>
              <th style={{ textAlign: "right", padding: "4px 6px", width: 110 }}>PREZZO €</th>
              <th style={{ textAlign: "center", padding: "4px 6px", width: 90 }}>LIBERO</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                <td style={{ padding: "3px 6px" }}>
                  <input type="text" value={r.nome} data-testid={`pmd-listino-nome-${i}`}
                    onChange={e => upd(i, { nome: e.target.value })}
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                </td>
                <td style={{ padding: "3px 6px" }}>
                  <input type="number" step="0.01" min="0" value={r.libero ? "" : r.prezzo} disabled={r.libero}
                    data-testid={`pmd-listino-prezzo-${i}`} placeholder={r.libero ? "variabile" : ""}
                    onChange={e => upd(i, { prezzo: e.target.value })}
                    style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, textAlign: "right", background: r.libero ? "#f5f5f5" : "white" }} />
                </td>
                <td style={{ textAlign: "center", padding: "3px 6px" }}>
                  <input type="checkbox" checked={!!r.libero} data-testid={`pmd-listino-libero-${i}`}
                    onChange={e => upd(i, { libero: e.target.checked })} title="Importo variabile digitato ogni volta" />
                </td>
                <td style={{ textAlign: "center" }}>
                  <button type="button" onClick={() => delRow(i)} data-testid={`pmd-listino-del-${i}`}
                    title="Rimuovi voce" style={{ border: "none", background: "transparent", color: "#c62828", cursor: "pointer", fontSize: 14 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addRow} data-testid="pmd-listino-add"
          style={{ marginTop: 10, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#00695c", background: "white", border: "1px dashed #00897b", borderRadius: 4, cursor: "pointer" }}>➕ Aggiungi voce</button>
        <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>Spunta «Libero» per le voci il cui importo si digita di volta in volta (es. pasto libero).</div>
      </div>
      <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
        {saved && <span data-testid="pmd-listino-saved" style={{ fontSize: 12, fontWeight: 700, color: "#2e7d32" }}>✓ Listino salvato</span>}
        <button type="button" onClick={save} disabled={busy} data-testid="pmd-listino-save"
          style={{ padding: "8px 20px", background: busy ? "#80cbc4" : "#00695c", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{busy ? "Salvataggio…" : "Salva listino"}</button>
      </div>
    </div>
  );
}

function PagamentiPopup({ nome, rows: initialRows, onSave, onClose }) {
  const [rows, setRows] = useState(() => (initialRows || []).map(x => ({ data: x.data || "", importo: x.importo != null ? x.importo : 0, nota: x.nota || "" })));
  const [busy, setBusy] = useState(false);
  const upd = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, { data: new Date().toISOString().slice(0, 10), importo: "", nota: "" }]);
  const delRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const totale = rows.reduce((s, r) => s + (Number(r.importo) || 0), 0);
  const save = async () => {
    setBusy(true);
    const ok = await onSave(rows.map(r => ({ data: (r.data || "").trim(), importo: Number(r.importo) || 0, nota: (r.nota || "").trim() })));
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10002, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div data-testid="pmd-pagamenti-modal" onMouseDown={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: 8, width: 680, maxWidth: "96vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", background: "#1565c0", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>💳 Pagamenti — {nome}</div>
          <button type="button" onClick={onClose} data-testid="pmd-pag-close" style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: "#607d8b", fontSize: 11 }}>
                <th style={{ textAlign: "left", padding: "4px 6px", width: 150 }}>DATA</th>
                <th style={{ textAlign: "right", padding: "4px 6px", width: 100 }}>IMPORTO €</th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>NOTA</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: "3px 6px" }}>
                    <input type="date" value={r.data} data-testid={`pmd-pag-data-${i}`}
                      onChange={e => upd(i, { data: e.target.value })}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <input type="number" step="0.01" min="0" value={r.importo} data-testid={`pmd-pag-importo-${i}`}
                      onChange={e => upd(i, { importo: e.target.value })}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <input type="text" value={r.nota} data-testid={`pmd-pag-nota-${i}`} placeholder="causale (es. mensa settembre)"
                      onChange={e => upd(i, { nota: e.target.value })}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    <button type="button" onClick={() => stampaRicevuta({ nome, importo: r.importo, nota: r.nota })} data-testid={`pmd-pag-ricevuta-${i}`}
                      title="Stampa ricevuta" style={{ border: "none", background: "transparent", color: "#1565c0", cursor: "pointer", fontSize: 15 }}>🖨</button>
                    <button type="button" onClick={() => delRow(i)} data-testid={`pmd-pag-del-${i}`}
                      title="Rimuovi" style={{ border: "none", background: "transparent", color: "#c62828", cursor: "pointer", fontSize: 14 }}>🗑</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 12, color: "#888", textAlign: "center" }}>Nessun pagamento registrato.</td></tr>}
            </tbody>
          </table>
          <button type="button" onClick={addRow} data-testid="pmd-pag-add"
            style={{ marginTop: 10, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#1565c0", background: "white", border: "1px dashed #1565c0", borderRadius: 4, cursor: "pointer" }}>➕ Aggiungi pagamento</button>
          <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>🖨 stampa una ricevuta generica non intestata per il pagamento della riga.</div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1565c0" }}>Totale: € {totale.toFixed(2)}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose} data-testid="pmd-pag-cancel"
              style={{ padding: "8px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}>Annulla</button>
            <button type="button" onClick={save} disabled={busy} data-testid="pmd-pag-save"
              style={{ padding: "8px 20px", background: busy ? "#90caf9" : "#1565c0", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{busy ? "Salvataggio…" : "Salva pagamenti"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
